import Icon, {icons} from './icon'
import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import Link from 'react-router/lib/Link'
import {colors, palettes, text} from '../styles'
import {css} from 'glamor'

const style = {
  chevronBase: css({
    color: colors.gray46,
    position: 'absolute',
    top: '49%',
    transform: 'translateY(-50%)'
  }),
  chevronLeft: css({
    left: '10px'
  }),
  chevronRight: css({
    right: '15px'
  }),
  listGroupItemRadio: css({
    background: colors.white,
    border: `1px solid ${colors.gray77}`,
    borderRadius: '10px',
    height: '20px',
    padding: '2px',
    position: 'absolute',
    right: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    '&[aria-selected="true"]:before': {
      background: palettes.primary.base,
      borderRadius: '7px',
      content: '""',
      height: '14px',
      position: 'absolute',
      right: '2px',
      top: '2px',
      width: '14px'
    }
  }),
  listGroup: css({
    margin: '0 -16px 20px',
    paddingLeft: '0'
  }),
  listGroupItemBase: css({
    backgroundColor: colors.white,
    border: '1px solid #ddd',
    cursor: 'pointer',
    display: 'block',
    position: 'relative',
    padding: '12px 15px',
    marginBottom: '-1px',
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      textDecoration: 'none'
    }
  }),
  listGroupHeading: css({
    paddingLeft: '30px'
  }),
  listGroupItemSpacer: css({
    background: colors.accentBG
  }),
  listGroupLink: css({
    color: colors.linkText,
    paddingLeft: '30px'
  }),
  listGroupSubheading: css({
    color: colors.grayMedium,
    background: colors.accentBG,
    fontWeight: '700'
  }),
  listGroupText: css(text.h4, {
    color: colors.gray20,
    cursor: 'default',
    fontWeight: '700',
    padding: '25px 15px',
    textAlign: 'center',
    textTransform: 'none',
    '& > span': {
      textTransform: 'capitalize'
    }
  })
}

function mapListItem(listItem) {
  if (listItem.List && listItem.DisplayName === null) {
    return (
      <Fragment key={listItem.Url}>{listItem.List.map(mapListItem)}</Fragment>
    )
  } else if (listItem.List) {
    return (
      <Fragment key={listItem.Url}>
        <ListGroup.Subheading>{listItem.DisplayName}</ListGroup.Subheading>
        {listItem.List.map(mapListItem)}
      </Fragment>
    )
  } else if (listItem.DisplayName === '') {
    return undefined
  } else if (listItem.onClick) {
    return (
      <ListGroup.Item key={listItem.DisplayName} onClick={listItem.onClick}>
        {listItem.DisplayName}
      </ListGroup.Item>
    )
  } else {
    return (
      <ListGroup.Item to={listItem.Url} key={listItem.Url}>
        {listItem.DisplayName}
      </ListGroup.Item>
    )
  }
}

export const ListGroup = ({id, list, description, label, onClick, to}) => (
  <ul className="list-group" id={id} {...style.listGroup}>
    {label && (
      <ListGroup.Heading label={label} to={to} onClick={onClick}>
        {label}
      </ListGroup.Heading>
    )}
    {description && <ListGroup.Item>{description}</ListGroup.Item>}
    {list.map(mapListItem)}
  </ul>
)

ListGroup.propTypes = {
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  id: PropTypes.string,
  label: PropTypes.string,
  list: PropTypes.array.isRequired,
  onClick: PropTypes.func,
  to: PropTypes.string
}

ListGroup.Heading = ({children, onClick, to}) => {
  return (
    <Link
      className="list-group-heading"
      to={to}
      onClick={onClick}
      {...css(style.listGroupItemBase, style.listGroupHeading)}
    >
      <Icon
        icon={icons.chevronLeft}
        {...css(style.chevronBase, style.chevronLeft)}
      />{' '}
      {children}
    </Link>
  )
}

ListGroup.Heading.displayName = 'ListGroup.Heading'
ListGroup.Heading.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  onClick: PropTypes.func,
  to: PropTypes.string
}

ListGroup.Subheading = ({children}) => (
  <li {...css(style.listGroupItemBase, style.listGroupSubheading)}>
    {children}
  </li>
)

ListGroup.Subheading.displayName = 'ListGroup.Subheading'
ListGroup.Subheading.propTypes = {
  children: PropTypes.node
}

ListGroup.Item = ({
  className,
  children,
  onClick,
  radio,
  selected,
  to,
  ...rest
}) => {
  let Component
  let elementClassName = className
    ? `${className} list-group-item`
    : 'list-group-item'
  let elementStyle

  if (to) {
    Component = Link
    elementStyle = css(style.listGroupItemBase, style.listGroupLink)
  } else if (onClick) {
    Component = 'button'
    elementStyle = css(style.listGroupItemBase, style.listGroupLink)
  } else {
    Component = 'li'
    elementStyle = css(style.listGroupItemBase, style.listGroupText)
  }

  return (
    <Component
      className={elementClassName}
      to={to}
      onClick={onClick}
      {...elementStyle}
      {...rest}
    >
      {children}
      {to || onClick ? (
        radio ? (
          <div aria-selected={selected} {...style.listGroupItemRadio} />
        ) : (
          <Icon
            icon={icons.chevronRight}
            {...css(style.chevronBase, style.chevronRight)}
          />
        )
      ) : undefined}
    </Component>
  )
}

ListGroup.Item.displayName = 'ListGroup.Item'
ListGroup.Item.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  heading: PropTypes.bool,
  radio: PropTypes.bool,
  selected: PropTypes.bool,
  to: PropTypes.string,
  onClick: PropTypes.func,
  subheading: PropTypes.bool
}

ListGroup.Item.defaultProps = {
  radio: false,
  selected: false
}

export default ListGroup
